import { takeEvery, takeLatest } from 'redux-saga'
import { call, put, take, fork, select, cancel, cancelled } from 'redux-saga/effects'
import actionTypes from 'redux/actions/actionTypes'
import { previewSync, privateLibrarySync } from 'sync'
import callAny from 'redux/sagas/helpers/call_any'
import { privateLibrarySelectors } from 'redux/selectors'
import privateLibraryActions from 'redux/actions/privateLibraryActions'
import myAccountActions from 'redux/actions/myAccountActions'
import browserHistory from 'helpers/history'
import entityTypes from 'helpers/enums/entityTypes'
import sessionTypes from 'helpers/enums/sessionTypes'
import { localize } from 'helpers/translator'
import formatError from './helpers/error_formatter'
import config from 'config'
import userDataJwtSelector from '../selectors/user/user_data_jwt_selector'
import userLibraryTypes from 'helpers/enums/userLibraryTypes'

const private_library_get_actions = [
	actionTypes.PRIVATE_LIBRARY_SETTINGS_FETCHED,
	actionTypes.PRIVATE_LIBRARY_LESSONS_SORT_UPDATE,
	actionTypes.PRIVATE_LIBRARY_CURRENT_APPLY_SEARCH,
	actionTypes.PRIVATE_LIBRARY_CURRENT_APPLY_REDIRECT_SEARCH,
	actionTypes.PRIVATE_LIBRARY_LESSONS_FILTERS_UPDATE,
	actionTypes.PRIVATE_LIBRARY_CURRENT_CLEAR_SEARCH,
	actionTypes.PRIVATE_LIBRARY_FIND_LESSONS_ENTITIES_SAVED,
	actionTypes.PRIVATE_LIBRARY_DELETE_CURRENT_FOLDER_SUCCESS,
	actionTypes.PRIVATE_LIBRARY_FOLDER_ADD_LESSON_SUCCESS,
	actionTypes.PRIVATE_LIBRARY_DELETE_LESSON_SUCCESS,
	actionTypes.PRIVATE_LIBRARY_LESSONS_PICKER_ADD_SUCCESS,
	actionTypes.PRIVATE_LIBRARY_FOLDER_REMOVE_LESSON_SUCCESS,
	actionTypes.PRIVATE_LIBRARY_FOLDER_MOVE_LESSON_SUCCESS,
]
const private_library_search_actions = [
	actionTypes.PRIVATE_LIBRARY_SEARCH_SORT_UPDATE,
	actionTypes.PRIVATE_LIBRARY_FIND_LESSONS_APPLY_SEARCH,
	actionTypes.PRIVATE_LIBRARY_FIND_LESSONS_CLEAR_SEARCH,
	actionTypes.PRIVATE_LIBRARY_SEARCH_FILTERS_UPDATE,
]

function *getLessons() {
	try {
		const entity_type = yield select(privateLibrarySelectors.getCurrentLibraryType)
		if (entity_type === 'ayce') {
			yield put(privateLibraryActions.initializeContentPackage())
		}
		const libraryData = yield select(privateLibrarySelectors.getLessons)
		if (!libraryData.more) {
			return
		}
		const response = yield callAny(privateLibrarySync.getLibraryLessons, libraryData.libraryUID, libraryData.folderUID, libraryData.search.page, libraryData.search.query.order_field, libraryData.search.query.order_type, libraryData.search.query.applied_keyword, libraryData.search.query.applied_filters)
		yield put({
			type: actionTypes.PRIVATE_LIBRARY_LESSONS_FETCHED,
			libraryUID: libraryData.libraryUID,
			entities: response.payload.entities,
			page: response.payload.page,
			more: response.payload.more,
		})
	} catch (error) {
		yield put({
			type: actionTypes.SYNC_UNCAUGHT_ERROR,
			payload: error.serverError,
		})
	}
}

function *getLessonsNextPage() {
	while (true) {
		const action = yield take([...private_library_get_actions, actionTypes.PRIVATE_LIBRARY_LESSONS_NEXT_PAGE])

		if (action.type !== actionTypes.PRIVATE_LIBRARY_LESSONS_NEXT_PAGE) {
			yield take([...private_library_get_actions, actionTypes.PRIVATE_LIBRARY_LESSONS_FETCHED])
		} else {
			try {
				const pageTask = yield fork(getLessons)
				const action = yield take([...private_library_get_actions, actionTypes.PRIVATE_LIBRARY_LESSONS_FETCHED])
				if (action.type !== actionTypes.PRIVATE_LIBRARY_LESSONS_FETCHED) {
					yield cancel(pageTask)
				}
			} catch (error) {
				// console.log(error)
			}
		}
	}
}

function *getSettings() {

	try {
		const libraryData = yield select(privateLibrarySelectors.getSettingData)
		const response = yield call(privateLibrarySync.getLibrarySettings, libraryData.libraryUID)
		if (response.error_code === 0) {
			yield put({
				type: actionTypes.PRIVATE_LIBRARY_SETTINGS_FETCHED,
				settings: response.payload.settings,
				libraryUID: libraryData.libraryUID,
			})
		} else {
			yield put({
				type: actionTypes.SYNC_UNCAUGHT_ERROR,
				payload: response.payload,
			})
		}
	} catch (error) {
		// console.log(error)
	}
}

function *getFolders() {
	const libraryData = yield select(privateLibrarySelectors.getSettingData)
	yield call(getFoldersData, libraryData.libraryUID, libraryData.parentId)
}

function *getFoldersByParentId(action) {
	const privateLibraryId = action.libraryType === userLibraryTypes.SCHOOL_LIBRARY ?
		action.lesson.data.actions.canAddToSchoolLibraryId : action.lesson.data.actions.canAddToDistrictLibraryId
	yield call(getFoldersData, privateLibraryId, action.folderId)
}

function *getFoldersData(libraryUID, parentId) {
	try {
		const response = yield callAny(privateLibrarySync.getLibraryFolders, libraryUID, parentId)
		const payload = response.payload
		yield put(privateLibraryActions.getPrivateLibraryFoldersDataSuccess(
			payload.folders,
			payload.name,
			payload.parents,
			payload.color,
			payload.isAdmin,
		))
	} catch (error) {
		yield put(privateLibraryActions.getPrivateLibraryFoldersDataFailed(formatError(error)))
	}
}

function *getPreview(action) {
	try {
		let response
		if (action.entity_type === 'lesson') {
			response = yield callAny(previewSync.getPreview, action.entity_ID, 'id', action.libraryUID)
		} else {
			response = yield callAny(previewSync.getBundle, action.entity_ID, 'id', action.libraryUID)
		}

		yield put({
			type: actionTypes.PRIVATE_LIBRARY_PREVIEW_FETCHED,
			response: response.payload,
		})

	} catch (error) {
		yield put({
			type: actionTypes.PRIVATE_LIBRARY_PREVIEW_INITIALIZE_ERROR,
			redirectUrl: config.app + 'library',
		})
	}
}

function *submitSettings() {
	while (true) {
		const action = yield take(actionTypes.PRIVATE_LIBRARY_SETTINGS_SUBMIT)
		try {
			const settingsData = yield select(privateLibrarySelectors.getSettingData)
			const response = yield call(privateLibrarySync.saveSettings, settingsData.libraryUID, action.values.name, action.values.users_can_add)
			if (response.error_code === 0) {
				yield put({
					type: actionTypes.PRIVATE_LIBRARY_SETTINGS_SUCCESS,
					settings: response.payload.settings,
				})
			}
		} catch (error) {
			// console.log(error)
		}
	}
}

function *getFilterDefinitions() {
	try {
		const filtersData = yield select(privateLibrarySelectors.filtersDefinitions)
		const response = yield callAny(privateLibrarySync.getFilterDefinitions, filtersData.privateLibraryId)
		yield put({
			type: actionTypes.PRIVATE_LIBRARY_FILTER_DEFINITIONS_FETCHED,
			data: response.payload,
		})
	} catch (error) {
		if (error.serverError) {
			yield put({
				type: actionTypes.PRIVATE_LIBRARY_LESSONS_ACCESS_DENIED,
				redirectUrl: `${config.app}library`,
				error: error.serverError.payload,
			})
		}

	}
}

function *searchLessons() {
	const searchData = yield select(privateLibrarySelectors.searchLessons)
	if (searchData.search.query.applied_keyword || searchData.search.query.applied_filters.length) {
		const response = yield call(privateLibrarySync.searchLibraryLessons, searchData.libraryUID, searchData.search.page, searchData.search.query.order_field, searchData.search.query.order_type, searchData.search.query.applied_keyword, searchData.search.query.applied_filters)
		if (response.error_code === 0) {
			yield put({
				type: actionTypes.PRIVATE_LIBRARY_SEARCH_FETCHED,
				libraryUID: searchData.libraryUID,
				entities: response.payload.entities,
				page: searchData.search.page,
				more: response.payload.more,
				total_entities_found: response.payload.total_entities_found,
				total_has_paid_lessons: response.payload.total_has_paid_lessons,
			})
		} else {
			yield put({
				type: actionTypes.SYNC_UNCAUGHT_ERROR,
				payload: response.payload,
			})
		}
	}
}

function *searchLessonsNextPage() {
	while (true) {
		const action = yield take([...private_library_search_actions, actionTypes.PRIVATE_LIBRARY_SEARCH_NEXT_PAGE])

		if (action.type !== actionTypes.PRIVATE_LIBRARY_SEARCH_NEXT_PAGE) {
			yield take([...private_library_search_actions, actionTypes.PRIVATE_LIBRARY_SEARCH_FETCHED])
		} else {
			try {
				const pageTask = yield fork(searchLessons)
				const action = yield take([...private_library_search_actions, actionTypes.PRIVATE_LIBRARY_SEARCH_FETCHED])
				if (action.type !== actionTypes.PRIVATE_LIBRARY_SEARCH_FETCHED) {
					yield cancel(pageTask)
				}
			} catch (error) {
				// console.log(error)
			}
		}
	}
}

function *addLessons() {
	const selectedLessons = yield select(privateLibrarySelectors.addLessons)
	let actionConfirmed = true
	let putAction = false
	let takeActions = []
	let libraryUIDs = selectedLessons.libraryUID
	let multipleLibraries = false

	if (selectedLessons.multipleSchools) {
		yield put(myAccountActions.searchSchools('', '0', '0', true))
		putAction = {
			type: actionTypes.PRIVATE_LIBRARY_FIND_LESSONS_ADD_LESSONS_MUTIPLE_SCHOOLS_CONFIRM,
			alertTitle: selectedLessons.alertTitle,
		}
		takeActions =[actionTypes.PRIVATE_LIBRARY_FIND_LESSONS_ADD_LESSONS_MULTIPLE_SCHOOLS_CONFIRMED, actionTypes.PRIVATE_LIBRARY_FIND_LESSONS_ADD_LESSONS_MULTIPLE_SCHOOLS_DENIED, actionTypes.PRIVATE_LIBRARY_FIND_LESSONS_ADD_LESSONS_MULTIPLE_SCHOOLS_CANCELLED]
	} else if (selectedLessons.hasPaidLessons) {
		putAction = {
			type: actionTypes.PRIVATE_LIBRARY_FIND_LESSONS_ADD_LESSONS_CONFIRM,
		}
		takeActions = [actionTypes.PRIVATE_LIBRARY_FIND_LESSONS_ADD_LESSONS_CONFIRMED, actionTypes.PRIVATE_LIBRARY_FIND_LESSONS_ADD_LESSONS_CANCELLED]
	}

	if (putAction) {
		yield put(
			putAction
		)
		const confirmResponse = yield take(takeActions)
		if (confirmResponse.type === actionTypes.PRIVATE_LIBRARY_FIND_LESSONS_ADD_LESSONS_CANCELLED || confirmResponse.type === actionTypes.PRIVATE_LIBRARY_FIND_LESSONS_ADD_LESSONS_MULTIPLE_SCHOOLS_CANCELLED) {
			actionConfirmed = false
		} else if (confirmResponse.type === actionTypes.PRIVATE_LIBRARY_FIND_LESSONS_ADD_LESSONS_MULTIPLE_SCHOOLS_CONFIRMED) {
			const privateLibrarySchoolIds = yield select(privateLibrarySelectors.multipleSchoolsSelector)
			multipleLibraries = true
			libraryUIDs = privateLibrarySchoolIds
		}
	}

	if (actionConfirmed) {
		yield put({
			type: actionTypes.PRIVATE_LIBRARY_FIND_LESSONS_ADD_LESSONS_GET_METRICS,
		})

		try {
			yield callAny(privateLibrarySync.searchSaveEntities, libraryUIDs, selectedLessons.folderUID, selectedLessons.selected_all, selectedLessons.lessons, selectedLessons.bundles, selectedLessons.searchData.query.order_field, selectedLessons.searchData.query.order_type, selectedLessons.searchData.query.applied_keyword, selectedLessons.searchData.query.applied_filters)
			yield put({
				type: actionTypes.PRIVATE_LIBRARY_FIND_LESSONS_ENTITIES_SAVED,
				lessons: selectedLessons.lessons,
				bundles: selectedLessons.bundles,
				multipleLibraries,
			})
		} catch (error) {
			// console.log(error)
		}
	}
}

function *nameFolderSubmit() {
	while (true) {
		yield take([actionTypes.PRIVATE_LIBRARY_NAME_FOLDER_SUBMIT])
		try {
			const folderData = yield select(privateLibrarySelectors.nameFolder)
			const response = yield callAny(privateLibrarySync.updateFolder, folderData.libraryUID, folderData.folderUID, folderData.value, folderData.productUid, folderData.productType, null, folderData.parentId)
			yield put(privateLibraryActions.nameFolderSuccess(response.payload, folderData.folderUID))
			if (folderData.folderUID === 0 && response.payload.entityUID) {
				browserHistory.push('/admin/libraries/' + folderData.libraryUID + '/folder/' + response.payload.entityUID)
				///folder/933
			}
		} catch (error) {
			yield put(privateLibraryActions.nameFolderError(error.serverError))
		}
	}
}

function *deleteCurrentFolder() {
	const current = yield select(privateLibrarySelectors.deleteCurrentFolderConfirm)
	try {
		const response = yield call(privateLibrarySync.deleteFolder, current.libraryUID, current.folderUID)
		if (response.error_code === 0) {
			yield put({
				type: actionTypes.PRIVATE_LIBRARY_DELETE_CURRENT_FOLDER_SUCCESS,
				data: response.payload,
			})
			if (response.payload.parentId != 0) {
				browserHistory.push(`/admin/libraries/${current.libraryUID}/folder/${response.payload.parentId}`)
			} else {
				browserHistory.push(`/admin/libraries/${current.libraryUID}`)
			}
		} else {
			yield put({
				type: actionTypes.SYNC_UNCAUGHT_ERROR,
				payload: response.payload,
			})
		}
	} catch (error) {
		// console.log(error)
	}
}

function *addLessonToFolder(action) {
	try {
		const libraryUID = yield select(privateLibrarySelectors.getCurrentLibraryUID)
		const response = yield callAny(privateLibrarySync.saveLessonsToFolder, action.folder_id, action.lessons, action.bundles, libraryUID)
		yield put(privateLibraryActions.addLessonToFolderSuccess(response.payload))
	} catch (error) {
		yield put(privateLibraryActions.nameFolderError(error.serverError))
	}
}

function *removeEntityFromFolder(action) {
	try {
		const response = yield call(privateLibrarySync.removeEntityFromFolder, action.entity, action.entityType, action.folderUID)

		if (response.error_code === 0) {
			yield put({
				type: actionTypes.PRIVATE_LIBRARY_FOLDER_REMOVE_LESSON_SUCCESS,
				entityUID: action.entityUID,
			})
		} else {
			yield put({
				type: actionTypes.PRIVATE_LIBRARY_FOLDER_REMOVE_LESSON_ERROR,
				message: response.message,
			})
		}

	} catch (error) {
		// console.log(error)
	}
}

function *changeFolderColor(action) {
	const changeFolderColorSelector = yield select(privateLibrarySelectors.changeFolderColor)
	try {
		const response = yield callAny(
			privateLibrarySync.updateFolder,
			changeFolderColorSelector.libraryUID,
			changeFolderColorSelector.folderUID,
			changeFolderColorSelector.folderName,
			null,
			null,
			action.colorID,
			changeFolderColorSelector.parentId)
		yield put(privateLibraryActions.changeFolderColorSuccess(
			response.payload.folders,
			response.payload.name,
			response.payload.color,
			response.payload.parents,
			response.payload.isAdmin
		))
	} catch (error) {
		yield put(privateLibraryActions.changeFolderColorError(error.serverError))
	}
}

function *doSearchLessons(searchParams) {
	try {
		if (searchParams.more) {
			const response = yield call(privateLibrarySync.getLibraryLessons, searchParams.entityUID, 0, searchParams.page, searchParams.order_field, searchParams.order_type, searchParams.search, undefined, undefined, searchParams.folderUID)
			if (response.error_code === 0) {
				yield put({
					type: actionTypes.LESSONS_SET_ALL,
					entities: response.payload.entities,
				})
				yield put({
					type: actionTypes.PRIVATE_LIBRARY_FOUND_LIBRARY_LESSONS,
					payload: {
						entities: response.payload.entities,
						page: response.payload.page,
						more: response.payload.more,
					},
				})
			} else {
				throw new Error('Uncaught error')
			}
		}
	} finally {
		if (yield cancelled()) {
		}
	}
}


function *lessonsPickerSearchLessons() {
	let currentSearchParams, currentRequest
	while (true) {
		yield take([actionTypes.PRIVATE_LIBRARY_LESSONS_PICKER_SEARCH_LESSONS, actionTypes.PRIVATE_LIBRARY_LESSONS_PICKER_INIT])
		try {
			const searchParams = yield select(privateLibrarySelectors.lessonsPickerModal)
			if (searchParams !== currentSearchParams) {
				currentSearchParams = searchParams
				if (currentRequest) {
					yield cancel(currentRequest)
				}
				currentRequest = yield fork(doSearchLessons, searchParams)
			}
		} catch (error) {
			// console.log(error)
		}
	}
}

function *lessonsPickerSaveLessons() {
	try {
		const searchParams = yield select(privateLibrarySelectors.lessonsPickerModalSave)
		yield callAny(privateLibrarySync.saveLessonsToFolder, searchParams.entityUID, searchParams.lessonsUIDs, searchParams.bundlesUIDs, searchParams.libraryUID)
		yield put(privateLibraryActions.lessonsPickerAddSuccess(searchParams.presentationUIDs))
		const message = (searchParams.bundlesUIDs.length + searchParams.lessonsUIDs.length) > 1 ? localize('admin_private_library_lessonsAddedToFolder') : localize('admin_private_library_lessonAddedToFolder')
		yield put({
			type: actionTypes.OPEN_TOP_INFORMATION,
			messageObject: {
				message,
				timeout: 3000,
				styleType: 'success',
			},
		})
	} catch (error) {
		yield put(privateLibraryActions.lessonsPickerAddError(error.serverError))
	}
}


function *deleteLesson(action) {
	try {
		let takeActions = []
		let actionConfirmed = false
		const deleteLessonData = yield select(privateLibrarySelectors.deleteLesson)
		const entity_id = action.entity.entityType === entityTypes.BUNDLE ? action.entity.entityUID : action.entity.entityID
		const multipleSchools = deleteLessonData.multipleSchools
		const actionConfirm = multipleSchools ? actionTypes.PRIVATE_LIBRARY_DELETE_LESSON_MULTIPLE_SCHOOLS_CONFIRM : actionTypes.PRIVATE_LIBRARY_DELETE_LESSON_CONFIRM
		yield put({
			type: actionConfirm,
			entity: entity_id,
			entityType: action.entity.entityType,
		})

		takeActions = [actionTypes.PRIVATE_LIBRARY_DELETE_LESSON_CONFIRMED, actionTypes.PRIVATE_LIBRARY_DELETE_LESSON_CANCELLED]
		if (multipleSchools) {
			yield put(myAccountActions.searchSchools('', '0', '0', true))
			takeActions = [actionTypes.PRIVATE_LIBRARY_DELETE_LESSON_CONFIRMED, actionTypes.PRIVATE_LIBRARY_DELETE_LESSON_MULTIPLE_SCHOOLS_CONFIRMED]
		}

		let	multipleLibraries = false
		let libraryUIDs = deleteLessonData.libraryUID
		const confirmAction = yield take(takeActions)
		if (confirmAction.type === actionTypes.PRIVATE_LIBRARY_DELETE_LESSON_CONFIRMED) {
			actionConfirmed = true
		} else if (confirmAction.type === actionTypes.PRIVATE_LIBRARY_DELETE_LESSON_MULTIPLE_SCHOOLS_CONFIRMED) {
			const privateLibrarySchoolIds = yield select(privateLibrarySelectors.multipleSchoolsSelector)
			actionConfirmed = true
			multipleLibraries = true
			libraryUIDs = privateLibrarySchoolIds
		}

		if (actionConfirmed) {
			try {
				yield callAny(privateLibrarySync.deleteLesson, libraryUIDs, entity_id, action.entity.entityType)
				yield put({
					type: actionTypes.PRIVATE_LIBRARY_DELETE_LESSON_SUCCESS,
					entity: action.entity,
					multipleLibraries,
				})
			} catch (error) {
				const serverError = formatError(error)
				yield put({
					type: actionTypes.PRIVATE_LIBRARY_DELETE_LESSON_ERROR,
					message: serverError.message,
				})
			}
		}

	} catch (error) {
		// console.log(error)
	}
}

function *addToPrivateLibrary(action) {
	try {
		let lessonArray = []
		let bundleArray = []
		if (action.entityType === 'bundle') {
			bundleArray = [action.entityID]
		} else {
			lessonArray = [action.entityID]
		}
		yield callAny(privateLibrarySync.searchSaveEntities, action.libraryId, 0, false, lessonArray, bundleArray)
		yield put({
			type: actionTypes.PRIVATE_LIBRARY_ADD_TO_PRIVATE_LIBRARY_REDIRECT,
			libraryType: action.libraryType,
			libraryId: action.libraryId,
			entityType: action.entityType,
		})
	} catch (error) {
		yield put({
			type: actionTypes.PRIVATE_LIBRARY_ADD_TO_PRIVATE_LIBRARY_ERROR,
		})

	}
}

function *moveLesson(action) {
	try {
		yield callAny(privateLibrarySync.saveLessonsToFolder, action.targetFolderID, action.lessons, action.bundles, action.libraryUID)
		yield put(privateLibraryActions.moveLessonSuccess())
	} catch (error) {
		yield put(privateLibraryActions.moveError(error.serverError))
	}
}

function *moveFolder(action) {
	try {
		yield callAny(privateLibrarySync.updateFolder, action.libraryUID, action.sourceFolderID, action.sourceName, null, null, null, action.targetFolderID)
		yield put(privateLibraryActions.moveFolderSuccess())
	} catch (error) {
		yield put(privateLibraryActions.moveError(error.serverError))
	}
}

// code repeated on my_library_sagas and store_sagas
function *launchSession(presentationUID, sessionType) {
	try {
		const jwt = yield select(userDataJwtSelector)
		return yield callAny(
			privateLibrarySync.launchSession,
			presentationUID,
			true,
			jwt,
			sessionType
		)
	} catch (error) {
		throw formatError(error)
	}
}

function *launchFrontOfTheClassroom(action) {
	try {
		const response = yield call(launchSession, action.presentation_uid, sessionTypes.FRONT_OF_CLASSROOM)
		yield put(privateLibraryActions.launchFrontOfTheClassroomSessionFetched(response))
	} catch (serverError) {
		yield put(privateLibraryActions.launchFrontOfTheClassroomSessionFailed(serverError))
	}
}

export default [
	fork(getLessonsNextPage),
	fork(searchLessonsNextPage),
	fork(submitSettings),
	takeEvery(actionTypes.PRIVATE_LIBRARY_INITIALIZE_PREVIEW, getPreview),
	takeLatest([
		actionTypes.PRIVATE_LIBRARY_INITIALIZE_FOLDER,
		actionTypes.PRIVATE_LIBRARY_DELETE_LESSON_SUCCESS,
		actionTypes.PRIVATE_LIBRARY_FIND_LESSONS_ENTITIES_SAVED,
	], getSettings),
	takeLatest([
		actionTypes.PRIVATE_LIBRARY_INITIALIZE_FOLDER,
		actionTypes.PRIVATE_LIBRARY_FOLDER_ADD_LESSON_SUCCESS,
	], getFolders),
	takeLatest(
		actionTypes.MY_LIBRARY_ADD_LESSON_TO_PRIVATE_LIBRARY_MODAL_FOLDER_ID_UPDATED,
		getFoldersByParentId),
	takeLatest(private_library_get_actions, getLessons),
	takeLatest(private_library_search_actions, searchLessons),
	takeEvery([
		actionTypes.PRIVATE_LIBRARY_INITIALIZE_FOLDER,
		actionTypes.PRIVATE_LIBRARY_INITIALIZE_PREVIEW,
	], getFilterDefinitions),
	takeEvery(actionTypes.PRIVATE_LIBRARY_FIND_LESSONS_ADD_LESSONS, addLessons),
	fork(nameFolderSubmit),
	takeLatest(actionTypes.PRIVATE_LIBRARY_DELETE_CURRENT_FOLDER_CONFIRM, deleteCurrentFolder),
	takeEvery(actionTypes.PRIVATE_LIBRARY_FOLDER_ADD_LESSON, addLessonToFolder),
	takeEvery(actionTypes.PRIVATE_LIBRARY_FOLDER_REMOVE_LESSON, removeEntityFromFolder),
	takeLatest(actionTypes.PRIVATE_LIBRARY_CHANGE_FOLDER_COLOR_CLICK, changeFolderColor),
	fork(lessonsPickerSearchLessons),
	takeLatest(actionTypes.PRIVATE_LIBRARY_LESSONS_PICKER_SAVE_LESSONS, lessonsPickerSaveLessons),
	takeLatest(actionTypes.PRIVATE_LIBRARY_DELETE_LESSON, deleteLesson),
	takeLatest(actionTypes.PRIVATE_LIBRARY_ADD_TO_PRIVATE_LIBRARY, addToPrivateLibrary),
	takeLatest(actionTypes.PRIVATE_LIBRARY_MOVE_LESSON_RELEASE, moveLesson),
	takeLatest(actionTypes.PRIVATE_LIBRARY_MOVE_FOLDER_RELEASE, moveFolder),
	takeLatest(actionTypes.PRIVATE_LIBRARY_PREVIEW_LAUNCH_FRONT_OF_THE_CLASSROOM, launchFrontOfTheClassroom),
]

export {
	moveLesson,
	moveFolder,
	launchSession,
	launchFrontOfTheClassroom,
}
